import { useFormContext, useWatch } from 'react-hook-form';
import { Checkbox } from '@vnetwork/ui/primitives';
import type { ContractFormValues } from '../contract-form';

/**
 * Per-config "Statement billable" toggle, bound to `<optPath>.statementable`.
 * Render this ONLY for statementable templates (see `isStatementable`) — for
 * other templates the flag is ignored by billing, so a toggle would be a false
 * affordance. The seed already defaults `statementable` to true for these
 * templates; this lets a salesperson opt a specific config OUT of monthly usage
 * reconciliation (đối soát).
 *
 * `compact` renders just the checkbox (for a table cell); otherwise a labelled row.
 */
export function StatementToggle({ optPath, compact = false }: { optPath: string; compact?: boolean }) {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const checked = useWatch({ control, name: `${optPath}.statementable` as never }) as unknown as boolean;

  const box = (
    <Checkbox
      checked={checked === true}
      onCheckedChange={(v) =>
        setValue(`${optPath}.statementable` as never, (v === true) as never, { shouldDirty: true })
      }
      aria-label="Statement billable — reconcile usage monthly"
    />
  );

  if (compact) return box;

  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer' }}>
      {box}
      <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)' }}>
        Statement billable
        <span style={{ display: 'block', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          Reconcile actual usage monthly against this commitment.
        </span>
      </span>
    </label>
  );
}
